/*
 * Render an address
 */
import {html, LitElement} from 'lit';
import {customElement, property} from 'lit/decorators.js';
import type {Addressable} from '../apis/api-common.ts';
import {
  arrowSvg,
  emailSvg,
  phoneSvg,
  showOnMapSvg,
} from '../helpers/elements.ts';
import {
  empty,
  normalizePhoneNumber,
  truncate,
  prettifyUrl,
} from '../helpers/string.ts';
import {classMap} from 'lit/directives/class-map.js';
import {Accommodation, Bicycle, Bookable} from '../apis/api-poi.ts';
import {ifDefined} from 'lit/directives/if-defined.js';
import {msg} from '@lit/localize';
import type {ShowOnMapDetail} from '../map/show-on-map-event.ts';
import type {LayertreeCode} from '../layers/layerTypes.ts';

@customElement('element-address')
export default class ElementAddress extends LitElement {
  @property({type: Object}) item: Addressable;
  @property({type: Boolean}) bigOverlay = false;
  @property({type: Boolean}) inPopup = false;

  showOnMap(item: Accommodation | Bicycle) {
    const showOnMapDetail: ShowOnMapDetail = {
      type: item instanceof Bicycle ? item.type : 'accommodation',
      id: item.id,
      refid: item.refid,
      types:
        item instanceof Accommodation
          ? (item.types as LayertreeCode[])
          : undefined,
    };
    this.dispatchEvent(
      new CustomEvent('showOnMap', {
        detail: showOnMapDetail,
        bubbles: true,
        composed: true,
      }),
    );
  }

  override render() {
    if (!this.item) return '';
    return html`
      <div
        class="${classMap({
          body: this.bigOverlay,
          flex: !this.bigOverlay,
          'flex-col': !this.bigOverlay,
          'gap-y-[2px]': !this.bigOverlay,
        })} not-italic"
      >
        ${this.bigOverlay
          ? html` <div>${this.item.addressName}</div>
              <div>${this.item.addressName2}</div>
              <div>${this.item.addressStreet}</div>
              <div>${this.item.addressZip} ${this.item.addressCity}</div>`
          : ''}
        <div
          class="${classMap({
            'gap-y-[2px]': !this.bigOverlay,
            'gap-y-[12px]': this.bigOverlay,
            'mt-[12px]': this.bigOverlay,
          })} flex flex-col"
        >
          <div
            data-cy="poi-phone"
            class="${classMap({
              'poi-address-item': !this.bigOverlay,
              hidden: empty(this.item.addressPhone),
            })}"
          >
            <a
              class="group flex w-full items-center link-with-icon-in-context mouse:hover:text-chm-focus-red"
              href="tel:${normalizePhoneNumber(this.item.addressPhone)}"
            >
              <div>
                ${phoneSvg(
                  'fill-chm-red mouse:group-hover:fill-chm-focus-red mr-2 w-[24px] h-[24px]',
                )}
              </div>
              <span class="overflow-hidden text-ellipsis whitespace-nowrap"
                >${this.item.addressPhone}</span
              >
            </a>
          </div>
          <div
            data-cy="poi-mobile"
            class="${classMap({
              'poi-address-item': !this.bigOverlay,
              hidden: empty(this.item.addressMobile),
            })}"
          >
            <a
              class="group flex w-full items-center link-with-icon-in-context mouse:hover:text-chm-focus-red"
              href="tel:${normalizePhoneNumber(this.item.addressMobile)}"
            >
              <div>
                ${phoneSvg(
                  'fill-chm-red mouse:group-hover:fill-chm-focus-red mr-2 w-[24px] h-[24px]',
                )}
              </div>
              <span class="overflow-hidden text-ellipsis whitespace-nowrap"
                >${this.item.addressMobile}</span
              >
            </a>
          </div>
          <div
            data-cy="poi-email"
            class="${classMap({
              'poi-address-item': !this.bigOverlay,
              hidden: empty(this.item.addressEmail),
            })}"
          >
            <a
              class="group flex w-full items-center link-with-icon-in-context mouse:hover:text-chm-focus-red"
              href="${`mailto:${this.item.addressEmail}`}"
            >
              <div>
                ${emailSvg(
                  'fill-chm-red mouse:group-hover:fill-chm-focus-red mr-2 w-[24px] h-[24px]',
                )}
              </div>
              <span class="overflow-hidden text-ellipsis whitespace-nowrap"
                >${this.item.addressEmail}</span
              >
            </a>
          </div>
          <div
            data-cy="poi-url"
            class="${classMap({
              'poi-address-item': !this.bigOverlay,
              hidden: !this.item.addressUrl || this.item instanceof Bookable,
            })}"
          >
            <a
              class="group flex w-full items-center link-with-icon-in-context mouse:hover:text-chm-focus-red"
              href=${ifDefined(this.item.addressUrl)}
              target="_blank"
              rel="noopener"
            >
              <div>
                ${arrowSvg({
                  classes:
                    'fill-chm-red mouse:group-hover:fill-chm-focus-red -rotate-45 mr-2 w-[24px] h-[24px]',
                })}
              </div>
              <span class="overflow-hidden text-ellipsis whitespace-nowrap">
                ${this.item.addressUrlText ||
                truncate(prettifyUrl(this.item.addressUrl))} </span
              ><span class="screen-reader-only"
                >${msg('(opens in a new tab)')}</span
              >
            </a>
          </div>
          ${(this.item instanceof Accommodation ||
            this.item instanceof Bookable) &&
          this.item.urlBookingLink
            ? html` <div
                data-cy="poi-booking-url"
                class="poi-address-item ${classMap({
                  hidden: !this.item.urlBookingLink,
                })}"
              >
                <a
                  class="group flex w-full items-center link-with-icon-in-context mouse:hover:text-chm-focus-red"
                  href="${this.item.urlBookingLink}"
                  target="_blank"
                  rel="noopener"
                >
                  <div>
                    ${arrowSvg({
                      classes:
                        'fill-chm-red mouse:group-hover:fill-chm-focus-red -rotate-45 mr-2 w-[24px] h-[24px]',
                    })}
                  </div>
                  <span class="overflow-hidden text-ellipsis whitespace-nowrap">
                    ${this.item.urlBookingText ||
                    truncate(prettifyUrl(this.item.urlBookingLink))}
                  </span>
                  <span class="screen-reader-only"
                    >${msg('(opens in a new tab)')}</span
                  >
                </a>
              </div>`
            : ''}
          ${!this.inPopup &&
          (this.item instanceof Accommodation || this.item instanceof Bicycle)
            ? html` <div
                class="poi-address-item cursor-pointer"
                @click="${() =>
                  this.showOnMap(this.item as Accommodation | Bicycle)}"
              >
                <div
                  class="group flex w-full items-center link-with-icon-in-context mouse:hover:text-chm-focus-red"
                >
                  <div>
                    ${showOnMapSvg(
                      'fill-chm-red mouse:group-hover:fill-chm-focus-red mr-2 w-[24px] h-[24px]',
                    )}
                  </div>
                  <span class="overflow-hidden"> ${msg('show on map')} </span>
                </div>
              </div>`
            : ''}
        </div>
      </div>
    `;
  }

  override createRenderRoot() {
    return this;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'element-address': ElementAddress;
  }
}
